import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  Layers3,
  Leaf,
  Plus,
  Search,
  Sprout,
  Warehouse,
} from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type {
  InputCatalogueItem,
  NurseryActivity,
  NurseryBed,
  NurserySeedlingLot,
  NurserySite,
  NurseryStockSummary,
  NurseryVariety,
  StaffDetail,
} from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

type ViewMode = "OVERVIEW" | "NURSERIES" | "STOCK" | "ACTIVITIES" | "VARIETIES";
type CreateMode = "SITE" | "BED" | "LOT" | "VARIETY" | undefined;

const number = new Intl.NumberFormat("en-UG");

export default function NurseryManagementPage() {
  const [sites, setSites] = useState<NurserySite[]>();
  const [beds, setBeds] = useState<NurseryBed[]>();
  const [lots, setLots] = useState<NurserySeedlingLot[]>();
  const [stock, setStock] = useState<NurseryStockSummary[]>();
  const [activities, setActivities] = useState<NurseryActivity[]>();
  const [varieties, setVarieties] = useState<NurseryVariety[]>();
  const [catalogue, setCatalogue] = useState<InputCatalogueItem[]>([]);
  const [staff, setStaff] = useState<StaffDetail[]>([]);
  const [mode, setMode] = useState<ViewMode>("OVERVIEW");
  const [createMode, setCreateMode] = useState<CreateMode>();
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);

  const [siteForm, setSiteForm] = useState({ code: "", name: "", responsibleEmployeeId: "", description: "" });
  const [bedForm, setBedForm] = useState({ nurseryId: "", code: "", name: "", capacity: "", description: "" });
  const [lotForm, setLotForm] = useState({
    nurseryId: "",
    bedId: "",
    seedlingItemId: "",
    sourceName: "",
    sourceLocation: "",
    propagationStartedDate: "",
    establishedDate: "",
    initialQuantity: "",
    notes: "",
  });
  const [varietyForm, setVarietyForm] = useState({
    code: "",
    name: "",
    sourceName: "",
    sourceRegion: "",
    description: "",
  });

  const load = async () => {
    try {
      const [siteRows, bedRows, lotRows, stockRows, activityRows, varietyRows, catalogueResult, staffResult] = await Promise.all([
        services.nursery.listSites(),
        services.nursery.listBeds(),
        services.nursery.listLots(),
        services.nursery.listStock(),
        services.nursery.listActivities(),
        services.nursery.listVarieties(),
        services.inputs.listCatalogue({ pageSize: 100 }),
        services.staff.list({ pageSize: 100 }),
      ]);
      setSites(siteRows);
      setBeds(bedRows);
      setLots(lotRows);
      setStock(stockRows);
      setActivities(activityRows);
      setVarieties(varietyRows);
      setCatalogue(catalogueResult.items);
      setStaff(staffResult.items);
      setError("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Nursery Management could not be loaded.");
    }
  };

  useEffect(() => { void load(); }, []);

  const nurseryItems = useMemo(
    () => catalogue.filter((item) => item.stockTrackingMode === "NURSERY" && item.status === "ACTIVE"),
    [catalogue],
  );

  const activeStaff = useMemo(
    () => staff.filter((member) => member.employmentStatus === "ACTIVE"),
    [staff],
  );

  const selectedSiteBeds = useMemo(
    () => (beds || []).filter((bed) => bed.nurseryId === lotForm.nurseryId && bed.status === "ACTIVE"),
    [beds, lotForm.nurseryId],
  );

  const filteredStock = useMemo(() => (stock || []).filter((row) =>
    `${row.nurseryName} ${row.bedName} ${row.lotCode} ${row.itemName} ${row.lifecycleStatus} ${row.varietyDetailStatus}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  ), [stock, search]);

  const filteredSites = useMemo(() => (sites || []).filter((row) =>
    `${row.nurseryCode} ${row.nurseryName} ${row.status}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  ), [sites, search]);

  const filteredActivities = useMemo(() => (activities || []).filter((row) =>
    `${row.nurseryName || ""} ${row.bedName || ""} ${row.lotCode || ""} ${row.activityName || ""} ${row.notes || ""}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  ), [activities, search]);

  const totalCurrent = (stock || []).reduce((sum, row) => sum + row.currentQuantity, 0);
  const totalReady = (stock || []).reduce((sum, row) => sum + row.readyQuantity, 0);
  const totalDistributed = (stock || []).reduce((sum, row) => sum + row.distributedQuantity, 0);
  const unclassifiedLots = (stock || []).filter((row) => row.varietyDetailStatus !== "COMPLETE").length;

  const createSite = async (event: FormEvent) => {
    event.preventDefault();
    if (!siteForm.code.trim() || !siteForm.name.trim()) {
      toast.error("Nursery code and name are required.");
      return;
    }
    setWorking(true);
    try {
      await services.nursery.createSite({
        nurseryCode: siteForm.code.trim(),
        nurseryName: siteForm.name.trim(),
        responsibleEmployeeId: siteForm.responsibleEmployeeId || undefined,
        description: siteForm.description.trim() || undefined,
      });
      toast.success("Nursery site created");
      setSiteForm({ code: "", name: "", responsibleEmployeeId: "", description: "" });
      setCreateMode(undefined);
      await load();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Nursery site could not be created.");
    } finally {
      setWorking(false);
    }
  };

  const createBed = async (event: FormEvent) => {
    event.preventDefault();
    if (!bedForm.nurseryId || !bedForm.code.trim() || !bedForm.name.trim()) {
      toast.error("Choose a nursery and enter the bed code and name.");
      return;
    }
    setWorking(true);
    try {
      await services.nursery.createBed({
        nurseryId: bedForm.nurseryId,
        bedCode: bedForm.code.trim(),
        bedName: bedForm.name.trim(),
        estimatedCapacity: bedForm.capacity ? Number(bedForm.capacity) : undefined,
        description: bedForm.description.trim() || undefined,
      });
      toast.success("Nursery bed created");
      setBedForm({ nurseryId: "", code: "", name: "", capacity: "", description: "" });
      setCreateMode(undefined);
      await load();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Nursery bed could not be created.");
    } finally {
      setWorking(false);
    }
  };

  const createLot = async (event: FormEvent) => {
    event.preventDefault();
    const initialQuantity = Number(lotForm.initialQuantity);
    if (!lotForm.nurseryId || !lotForm.bedId || !lotForm.seedlingItemId || !Number.isFinite(initialQuantity) || initialQuantity <= 0) {
      toast.error("Nursery, bed, seedling item and a positive starting quantity are required.");
      return;
    }
    setWorking(true);
    try {
      await services.nursery.createLot({
        nurseryId: lotForm.nurseryId,
        bedId: lotForm.bedId,
        seedlingItemId: lotForm.seedlingItemId,
        sourceName: lotForm.sourceName.trim() || undefined,
        sourceLocation: lotForm.sourceLocation.trim() || undefined,
        propagationStartedDate: lotForm.propagationStartedDate || undefined,
        establishedDate: lotForm.establishedDate || undefined,
        initialQuantity,
        notes: lotForm.notes.trim() || undefined,
      });
      toast.success("Seedling lot created", {
        description: "Variety detail is optional and can be added progressively later.",
      });
      setLotForm({
        nurseryId: "", bedId: "", seedlingItemId: "", sourceName: "", sourceLocation: "",
        propagationStartedDate: "", establishedDate: "", initialQuantity: "", notes: "",
      });
      setCreateMode(undefined);
      await load();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Seedling lot could not be created.");
    } finally {
      setWorking(false);
    }
  };

  const createVariety = async (event: FormEvent) => {
    event.preventDefault();
    if (!varietyForm.code.trim() || !varietyForm.name.trim()) {
      toast.error("Variety code and name are required.");
      return;
    }
    setWorking(true);
    try {
      await services.nursery.createVariety({
        varietyCode: varietyForm.code.trim(),
        varietyName: varietyForm.name.trim(),
        coffeeType: "ROBUSTA",
        sourceName: varietyForm.sourceName.trim() || undefined,
        sourceRegion: varietyForm.sourceRegion.trim() || undefined,
        description: varietyForm.description.trim() || undefined,
      });
      toast.success("Robusta variety added");
      setVarietyForm({ code: "", name: "", sourceName: "", sourceRegion: "", description: "" });
      setCreateMode(undefined);
      await load();
    } catch (reason) {
      toast.error(reason instanceof Error ? reason.message : "Variety could not be created.");
    } finally {
      setWorking(false);
    }
  };

  if (error) return <ErrorState message={error} />;
  if (!sites || !beds || !lots || !stock || !activities || !varieties) {
    return <LoadingState label="Loading Nursery Management" />;
  }

  return (
    <div className="space-y-6" data-testid="nursery-management-page">
      <PageHeader
        eyebrow="Inputs & Stock"
        title="Nursery Management"
        description="Manage living seedling stock from nursery establishment through survival, readiness and handover to Plot-level distribution."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setCreateMode("BED")}><Plus className="mr-2 size-4" />New Bed</Button>
            <Button variant="outline" onClick={() => setCreateMode("LOT")}><Sprout className="mr-2 size-4" />New Seedling Lot</Button>
            <Button onClick={() => setCreateMode("SITE")}><Plus className="mr-2 size-4" />New Nursery</Button>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card><CardContent className="p-5"><Sprout className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{number.format(totalCurrent)}</p><p className="text-sm text-muted-foreground">Current viable seedlings</p></CardContent></Card>
        <Card><CardContent className="p-5"><Leaf className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{number.format(totalReady)}</p><p className="text-sm text-muted-foreground">Distribution-ready</p></CardContent></Card>
        <Card><CardContent className="p-5"><Warehouse className="size-5 text-primary" /><p className="mt-3 text-3xl font-semibold">{number.format(totalDistributed)}</p><p className="text-sm text-muted-foreground">Distributed from nursery</p></CardContent></Card>
        <Card><CardContent className="p-5"><Layers3 className="size-5 text-warning" /><p className="mt-3 text-3xl font-semibold">{unclassifiedLots}</p><p className="text-sm text-muted-foreground">Lots with incomplete variety detail</p></CardContent></Card>
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border bg-card p-2 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap rounded-xl bg-muted/60 p-1">
          {([
            ["OVERVIEW", "Overview"],
            ["NURSERIES", "Nurseries & Beds"],
            ["STOCK", "Living Stock"],
            ["ACTIVITIES", "Activities"],
            ["VARIETIES", "Varieties"],
          ] as Array<[ViewMode, string]>).map(([value, label]) => (
            <Button key={value} type="button" size="sm" variant={mode === value ? "default" : "ghost"} onClick={() => setMode(value)}>
              {label}
            </Button>
          ))}
        </div>
        <div className="relative w-full lg:max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearch(event.target.value)} className="pl-9" placeholder="Search Nursery Management" />
        </div>
      </div>

      {createMode && (
        <Card className="border-primary/25" data-testid="nursery-create-panel">
          <CardHeader>
            <CardTitle className="text-base">
              {createMode === "SITE" && "Create Nursery Site"}
              {createMode === "BED" && "Create Nursery Bed"}
              {createMode === "LOT" && "Create Seedling Lot"}
              {createMode === "VARIETY" && "Add Robusta Variety"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {createMode === "SITE" && (
              <form onSubmit={createSite} className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Nursery code</Label><Input value={siteForm.code} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSiteForm((v) => ({ ...v, code: e.target.value }))} placeholder="OMAGORO-01" /></div>
                <div className="space-y-2"><Label>Nursery name</Label><Input value={siteForm.name} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSiteForm((v) => ({ ...v, name: e.target.value }))} placeholder="Omagoro Nursery" /></div>
                <div className="space-y-2">
                  <Label>Responsible staff</Label>
                  <Select value={siteForm.responsibleEmployeeId} onValueChange={(value: string) => setSiteForm((v) => ({ ...v, responsibleEmployeeId: value }))}>
                    <SelectTrigger><SelectValue placeholder="Optional responsible staff" /></SelectTrigger>
                    <SelectContent>{activeStaff.map((member) => <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Description</Label><Input value={siteForm.description} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSiteForm((v) => ({ ...v, description: e.target.value }))} placeholder="Optional site note" /></div>
                <div className="md:col-span-2 flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setCreateMode(undefined)}>Cancel</Button><Button disabled={working}>Create Nursery</Button></div>
              </form>
            )}

            {createMode === "BED" && (
              <form onSubmit={createBed} className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Nursery</Label>
                  <Select value={bedForm.nurseryId} onValueChange={(value: string) => setBedForm((v) => ({ ...v, nurseryId: value }))}>
                    <SelectTrigger><SelectValue placeholder="Choose Nursery" /></SelectTrigger>
                    <SelectContent>{sites.filter((site) => site.status === "ACTIVE").map((site) => <SelectItem key={site.id} value={site.id}>{site.nurseryName}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Bed code</Label><Input value={bedForm.code} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setBedForm((v) => ({ ...v, code: e.target.value }))} placeholder="BED-01" /></div>
                <div className="space-y-2"><Label>Bed name</Label><Input value={bedForm.name} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setBedForm((v) => ({ ...v, name: e.target.value }))} placeholder="Propagation Bed 1" /></div>
                <div className="space-y-2"><Label>Estimated capacity</Label><Input type="number" min="0" value={bedForm.capacity} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setBedForm((v) => ({ ...v, capacity: e.target.value }))} placeholder="10000" /></div>
                <div className="md:col-span-2 space-y-2"><Label>Description</Label><Textarea value={bedForm.description} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setBedForm((v) => ({ ...v, description: e.target.value }))} /></div>
                <div className="md:col-span-2 flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setCreateMode(undefined)}>Cancel</Button><Button disabled={working}>Create Bed</Button></div>
              </form>
            )}

            {createMode === "LOT" && (
              <form onSubmit={createLot} className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Nursery</Label>
                  <Select value={lotForm.nurseryId} onValueChange={(value: string) => setLotForm((v) => ({ ...v, nurseryId: value, bedId: "" }))}>
                    <SelectTrigger><SelectValue placeholder="Choose Nursery" /></SelectTrigger>
                    <SelectContent>{sites.filter((site) => site.status === "ACTIVE").map((site) => <SelectItem key={site.id} value={site.id}>{site.nurseryName}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bed</Label>
                  <Select value={lotForm.bedId} onValueChange={(value: string) => setLotForm((v) => ({ ...v, bedId: value }))} disabled={!lotForm.nurseryId}>
                    <SelectTrigger><SelectValue placeholder="Choose Bed" /></SelectTrigger>
                    <SelectContent>{selectedSiteBeds.map((bed) => <SelectItem key={bed.id} value={bed.id}>{bed.bedName}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Seedling catalogue item</Label>
                  <Select value={lotForm.seedlingItemId} onValueChange={(value: string) => setLotForm((v) => ({ ...v, seedlingItemId: value }))}>
                    <SelectTrigger><SelectValue placeholder="Choose Nursery-tracked seedling item" /></SelectTrigger>
                    <SelectContent>{nurseryItems.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Initial seedlings</Label><Input type="number" min="1" value={lotForm.initialQuantity} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLotForm((v) => ({ ...v, initialQuantity: e.target.value }))} placeholder="8000" /></div>
                <div className="space-y-2"><Label>Propagation started</Label><Input type="date" value={lotForm.propagationStartedDate} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLotForm((v) => ({ ...v, propagationStartedDate: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Established date</Label><Input type="date" value={lotForm.establishedDate} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLotForm((v) => ({ ...v, establishedDate: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Source</Label><Input value={lotForm.sourceName} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLotForm((v) => ({ ...v, sourceName: e.target.value }))} placeholder="Optional source / supplier" /></div>
                <div className="space-y-2"><Label>Source location</Label><Input value={lotForm.sourceLocation} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLotForm((v) => ({ ...v, sourceLocation: e.target.value }))} placeholder="e.g. Masaka" /></div>
                <div className="md:col-span-2 space-y-2"><Label>Notes</Label><Textarea value={lotForm.notes} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLotForm((v) => ({ ...v, notes: e.target.value }))} placeholder="Variety is optional. You can continue monitoring this lot without classifying varieties." /></div>
                <div className="md:col-span-2 flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setCreateMode(undefined)}>Cancel</Button><Button disabled={working}>Create Seedling Lot</Button></div>
              </form>
            )}

            {createMode === "VARIETY" && (
              <form onSubmit={createVariety} className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2"><Label>Variety code</Label><Input value={varietyForm.code} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setVarietyForm((v) => ({ ...v, code: e.target.value }))} placeholder="KR10 / D / H / A" /></div>
                <div className="space-y-2"><Label>Variety name</Label><Input value={varietyForm.name} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setVarietyForm((v) => ({ ...v, name: e.target.value }))} placeholder="Robusta variety name" /></div>
                <div className="space-y-2"><Label>Source</Label><Input value={varietyForm.sourceName} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setVarietyForm((v) => ({ ...v, sourceName: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Source region</Label><Input value={varietyForm.sourceRegion} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setVarietyForm((v) => ({ ...v, sourceRegion: e.target.value }))} placeholder="Masaka / other" /></div>
                <div className="md:col-span-2 space-y-2"><Label>Description</Label><Textarea value={varietyForm.description} onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setVarietyForm((v) => ({ ...v, description: e.target.value }))} /></div>
                <div className="md:col-span-2 flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setCreateMode(undefined)}>Cancel</Button><Button disabled={working}>Add Variety</Button></div>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {mode === "OVERVIEW" && (
        <div className="grid gap-5 xl:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="text-base">Nursery sites</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {sites.slice(0, 6).map((site) => (
                <div key={site.id} className="flex items-center justify-between rounded-xl border p-4">
                  <div><p className="font-semibold">{site.nurseryName}</p><p className="text-xs text-muted-foreground">{site.nurseryCode} · {site.bedCount} beds</p></div>
                  <div className="text-right"><p className="font-semibold">{number.format(site.currentSeedlings)}</p><p className="text-xs text-muted-foreground">{number.format(site.readySeedlings)} ready</p></div>
                </div>
              ))}
              {sites.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No nursery sites yet.</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Living stock requiring attention</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {stock.filter((row) => row.varietyDetailStatus !== "COMPLETE" || row.lifecycleStatus === "HARDENING").slice(0, 8).map((row) => (
                <Link key={row.lotId} to={`/nursery/lots/${row.lotId}`} className="block rounded-xl border p-4 transition-colors hover:bg-muted/30">
                  <div className="flex items-start justify-between gap-4">
                    <div><p className="font-semibold">{row.lotCode}</p><p className="text-xs text-muted-foreground">{row.nurseryName} · {row.bedName}</p></div>
                    <StatusBadge status={row.lifecycleStatus} testId={`nursery-overview-status-${row.lotId}`} />
                  </div>
                  <div className="mt-3 flex gap-5 text-sm"><span>{number.format(row.currentQuantity)} viable</span><span>{number.format(row.readyQuantity)} ready</span><span className="text-muted-foreground">Variety: {row.varietyDetailStatus.replace(/_/g, " ")}</span></div>
                </Link>
              ))}
              {stock.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Create the first seedling lot to begin monitoring.</p>}
            </CardContent>
          </Card>
        </div>
      )}

      {mode === "NURSERIES" && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Nursery</TableHead><TableHead>Beds</TableHead><TableHead>Viable seedlings</TableHead><TableHead>Ready</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredSites.map((site) => (
                  <TableRow key={site.id}>
                    <TableCell><p className="font-semibold">{site.nurseryName}</p><p className="text-xs text-muted-foreground">{site.nurseryCode}</p></TableCell>
                    <TableCell>{site.bedCount}</TableCell><TableCell>{number.format(site.currentSeedlings)}</TableCell><TableCell>{number.format(site.readySeedlings)}</TableCell>
                    <TableCell><StatusBadge status={site.status} testId={`nursery-site-status-${site.id}`} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {mode === "STOCK" && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Lot</TableHead><TableHead>Nursery / Bed</TableHead><TableHead>Initial</TableHead><TableHead>Current</TableHead><TableHead>Ready</TableHead><TableHead>Distributed</TableHead><TableHead>Variety detail</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filteredStock.map((row) => (
                    <TableRow key={row.lotId}>
                      <TableCell><Link to={`/nursery/lots/${row.lotId}`} className="font-semibold text-primary hover:underline">{row.lotCode}</Link><p className="text-xs text-muted-foreground">{row.itemName}</p></TableCell>
                      <TableCell>{row.nurseryName}<p className="text-xs text-muted-foreground">{row.bedName}</p></TableCell>
                      <TableCell>{number.format(row.initialQuantity)}</TableCell><TableCell>{number.format(row.currentQuantity)}</TableCell><TableCell>{number.format(row.readyQuantity)}</TableCell><TableCell>{number.format(row.distributedQuantity)}</TableCell>
                      <TableCell><span className={row.varietyDetailStatus === "COMPLETE" ? "text-primary" : "text-muted-foreground"}>{row.varietyDetailStatus.replace(/_/g, " ")}</span></TableCell>
                      <TableCell><StatusBadge status={row.lifecycleStatus} testId={`nursery-lot-status-${row.lotId}`} /></TableCell>
                    </TableRow>
                  ))}
                  {filteredStock.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">No living seedling stock matches this view.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === "ACTIVITIES" && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Activity</TableHead><TableHead>Nursery / Bed</TableHead><TableHead>Lot</TableHead><TableHead>Responsible</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredActivities.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{new Date(row.performedAt).toLocaleString()}</TableCell><TableCell className="font-medium">{row.activityName || row.activityCode}</TableCell>
                    <TableCell>{row.nurseryName || "Nursery"}{row.bedName && <p className="text-xs text-muted-foreground">{row.bedName}</p>}</TableCell>
                    <TableCell>{row.lotCode || "Whole nursery/bed"}</TableCell><TableCell>{row.responsibleEmployeeName || "Not specified"}</TableCell><TableCell className="max-w-sm truncate">{row.notes || "—"}</TableCell>
                  </TableRow>
                ))}
                {filteredActivities.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No nursery activities recorded yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {mode === "VARIETIES" && (
        <Card>
          <CardHeader className="flex-row items-center justify-between"><div><CardTitle className="text-base">Robusta Variety Registry</CardTitle><p className="mt-1 text-sm text-muted-foreground">Optional classification. Nursery work is never blocked when variety is unknown.</p></div><Button size="sm" onClick={() => setCreateMode("VARIETY")}><Plus className="mr-2 size-4" />Add Variety</Button></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Variety</TableHead><TableHead>Type</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {varieties.map((row) => (
                  <TableRow key={row.id}><TableCell className="font-medium">{row.varietyCode}</TableCell><TableCell>{row.varietyName}</TableCell><TableCell>{row.coffeeType}</TableCell><TableCell>{[row.sourceName,row.sourceRegion].filter(Boolean).join(" · ") || "Not recorded"}</TableCell><TableCell>{row.active ? "Active" : "Inactive"}</TableCell></TableRow>
                ))}
                {varieties.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">No varieties recorded yet. This does not prevent Nursery Management.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
